import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, TextField as MuiTextField, styled, Typography } from '@mui/material';
import React from 'react';
import { EColors, getColor } from 'theme';

type LabeledTextFieldProps = {
    label?: string;
    placeholder?: string;
    type?: React.HTMLInputTypeAttribute;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    onDelete?: () => void;
    disabled?: boolean;
    error?: string;
    multiline?: boolean;
    rows?: number;
    autoFocus?: boolean;
};

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const StyledTextField = styled(MuiTextField, {
    shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError }) => ({
    width: '100%',
    backgroundColor: getColor(EColors.pureWhite),
    borderRadius: '12px',

    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${hasError ? getColor(EColors.v3Error) : getColor(EColors.v3Dark)} !important`,
    },

    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${hasError ? getColor(EColors.v3Error) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },

    '& .MuiOutlinedInput-root': {
        backgroundColor: getColor(EColors.pureWhite),
        borderRadius: '12px',

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: hasError ? getColor(EColors.v3Error) : getColor(EColors.v3Dark),
            borderWidth: '1px',
        },

        '&.Mui-disabled': {
            backgroundColor: getColor(EColors.containerBackground),
        },
    },

    '& .MuiOutlinedInput-input': {
        backgroundColor: getColor(EColors.pureWhite),
        borderRadius: '12px',
        padding: '12.5px',
        paddingLeft: '16px',
        fontSize: '0.875rem',
        fontFamily: '"Neue Montreal", Roboto, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        color: getColor(EColors.v3TextPrimary),

        '&::placeholder': {
            color: getColor(EColors.v3TextSecondary),
        },
    },

    '& .MuiFormLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        fontFamily: '"Neue Montreal", Roboto, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        color: getColor(EColors.v3TextSecondary),
    },

    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.v3TextSecondary),
        fontSize: '0.875rem',
        transform: 'translate(9px, -11px) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1em',
    },
}));

const ErrorText = styled(Typography)({
    fontSize: '0.75rem',
    color: getColor(EColors.v3Error),
    fontFamily: '"General Sans", Roboto, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    marginTop: '4px',
});

const InputWrapper = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
});

const DeleteButton = styled(IconButton)({
    padding: '4px',
    color: getColor(EColors.v3TextSecondary),

    '&:hover': {
        color: getColor(EColors.v3Error),
        backgroundColor: getColor(EColors.v3Error, 0.1),
    },
});

export const LabeledTextField = ({
    label,
    placeholder,
    type = 'text',
    value,
    onChange,
    onBlur,
    onKeyDown,
    onDelete,
    disabled = false,
    error,
    multiline = false,
    rows,
    autoFocus = false,
}: LabeledTextFieldProps) => {
    return (
        <Container>
            <InputWrapper>
                <StyledTextField
                    label={label}
                    type={type}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    hasError={!!error}
                    multiline={multiline}
                    rows={rows}
                    autoFocus={autoFocus}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                {onDelete && !disabled && (
                    <DeleteButton onClick={onDelete} size="small" aria-label="Delete">
                        <CloseIcon fontSize="small" />
                    </DeleteButton>
                )}
            </InputWrapper>
            {error && <ErrorText>{error}</ErrorText>}
        </Container>
    );
};
